import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button } from "reactstrap";
import useToggleValueWithLocalStoragePersistence from "../common/hooks/useToggleValueWithLocalStoragePersistence";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlaylistIndexLoader } from "./PlaylistIndexLoader";
import PlaylistData from "../playlist/data/PlaylistData";
import { useStateWithLocalStoragePersistenceAndDefault } from "../common/hooks/useStateWithLocalStoragePersistenceAndDefault";
import { CyPlaylistIndex } from "./CyPlaylistIndex";
import { PlaylistIndexTable } from "./PlaylistIndexTable";
import { Link } from "react-router-dom";
import { PlaylistIndexSearchEngine } from "./PlaylistIndexSearchEngine";
import { defaultAmountOfEntriesInPlaylistIndex } from "./DefaultAmountOfEntriesInPlaylistIndex";
import { LocalStorageKey } from "../localStoragePersistence/common/LocalStorageKey";
import { NewPlaylistFab } from "./NewPlaylistFab";
import { LookupSongFunction } from "../common/LookupSongFunction";
import { useTranslation } from "react-i18next";

interface Props {
  lookupSong: LookupSongFunction;
  currentCrewIdentifier: string | undefined;
  setCurrentCrewIdentifier: (i: string | undefined) => void;
  setIsMainMenuOpen: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
}

const playlistIndexLoader = new PlaylistIndexLoader();

const lskOrderByField = new LocalStorageKey("PlaylistIndex", "orderByField");
const lskOrderByAscDesc = new LocalStorageKey(
  "PlaylistIndex",
  "orderByAscOrDesc",
);
const lskShowDetailsInPlaylistSummary = new LocalStorageKey(
  "PlaylistIndex",
  "showDetailsInPlaylistSummary",
);
const lskAmountOfEntriesInPlaylistIndex = new LocalStorageKey(
  "PlaylistIndex",
  "amountOfEntriesInPlaylistIndex",
);

export function PlaylistIndex({
  currentCrewIdentifier,
  setCurrentCrewIdentifier,
  lookupSong,
  setIsMainMenuOpen,
}: Props) {
  const { t } = useTranslation();

  const [orderByField, setOrderByField] =
    useStateWithLocalStoragePersistenceAndDefault<keyof PlaylistData>(
      lskOrderByField,
      "updatedAt",
    );
  const [orderByAscOrDesc, setOrderByAscOrDesc] =
    useStateWithLocalStoragePersistenceAndDefault<"asc" | "desc">(
      lskOrderByAscDesc,
      "desc",
    );

  const [playlistIndexData, setPlaylistIndexData] = useState<
    ReadonlyArray<PlaylistData>
  >([]);

  const [selectedPlaylists, setSelectedPlaylists] = useState<
    ReadonlyArray<PlaylistData>
  >([]);

  const [playlistIndexSearchEngine, setPlaylistIndexSearchEngine] =
    useState<PlaylistIndexSearchEngine>();

  const [showDetailsInPlaylistSummary, setShowDetailsInPlaylistSummary] =
    useToggleValueWithLocalStoragePersistence(
      lskShowDetailsInPlaylistSummary,
      true,
    );

  const [amountOfEntriesInPlaylistIndex, setAmountOfEntriesInPlaylistIndex] =
    useStateWithLocalStoragePersistenceAndDefault<number>(
      lskAmountOfEntriesInPlaylistIndex,
      defaultAmountOfEntriesInPlaylistIndex,
    );

  const playlistIndexTableHeaderRef: React.RefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);

  const slicedPlaylistData = useMemo(
    () => playlistIndexData.slice(0, amountOfEntriesInPlaylistIndex),
    [playlistIndexData, amountOfEntriesInPlaylistIndex],
  );

  const performIndexRefresh = useCallback(() => {
    const orderedPlaylistIndex = playlistIndexLoader.orderBy(
      playlistIndexLoader.loadAll(currentCrewIdentifier),
      orderByField,
      orderByAscOrDesc,
    );
    setPlaylistIndexSearchEngine(
      new PlaylistIndexSearchEngine(orderedPlaylistIndex),
    );
    setPlaylistIndexData(orderedPlaylistIndex);
  }, [orderByAscOrDesc, orderByField, currentCrewIdentifier]);

  useEffect(() => {
    performIndexRefresh();
  }, [currentCrewIdentifier, performIndexRefresh]);

  function changeOrderBy(newField: keyof PlaylistData) {
    const newAscOrDesc =
      newField !== orderByField
        ? "asc"
        : orderByAscOrDesc === "asc"
          ? "desc"
          : "asc";
    const orderedPlaylistIndex = playlistIndexLoader.orderBy(
      playlistIndexData,
      newField,
      newAscOrDesc,
    );
    setPlaylistIndexData(orderedPlaylistIndex);
    setOrderByField(newField);
    setOrderByAscOrDesc(newAscOrDesc);
  }

  const performSearch = useCallback(
    (searchTerm: string) => {
      const results: readonly PlaylistData[] = playlistIndexSearchEngine
        ? playlistIndexSearchEngine.search(searchTerm)
        : [];
      setPlaylistIndexData(results);
      playlistIndexTableHeaderRef.current?.scrollIntoView();
    },
    [playlistIndexSearchEngine],
  );

  return (
    <>
      <PlaylistIndexTable
        changeOrderBy={changeOrderBy}
        playlistIndexData={slicedPlaylistData}
        orderByAscOrDesc={orderByAscOrDesc}
        orderByField={orderByField}
        showDetailsInPlaylistSummary={showDetailsInPlaylistSummary}
        performSearch={performSearch}
        playlistIndexTableHeaderRef={playlistIndexTableHeaderRef}
        selectedPlaylists={selectedPlaylists}
        setSelectedPlaylists={setSelectedPlaylists}
        currentCrewIdentifier={currentCrewIdentifier}
        setCurrentCrewIdentifier={setCurrentCrewIdentifier}
        performIndexRefresh={performIndexRefresh}
        lookupSong={lookupSong}
        setShowDetailsInPlaylistSummary={setShowDetailsInPlaylistSummary}
        amountOfEntriesInPlaylistIndex={amountOfEntriesInPlaylistIndex}
        setAmountOfEntriesInPlaylistIndex={setAmountOfEntriesInPlaylistIndex}
        setIsMainMenuOpen={setIsMainMenuOpen}
      />
      {amountOfEntriesInPlaylistIndex < playlistIndexData.length && (
        <Alert color="warning" fade={false} className="mt-2 mb-0">
          <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
          {t("playlist.index.limitWarning", {
            count: amountOfEntriesInPlaylistIndex,
          })}
          <Button
            color={"link"}
            to={""}
            tag={Link}
            onClick={() => setAmountOfEntriesInPlaylistIndex(1000)}
            data-cy={CyPlaylistIndex.ShowAllPlaylistsLink}
          >
            {t("playlist.index.showAll")}
          </Button>
        </Alert>
      )}

      <NewPlaylistFab currentCrewIdentifier={currentCrewIdentifier} />
    </>
  );
}
